type EndpointValue = string | ((...args: unknown[]) => string);

type RecursiveEndpoints = {
	[key: string]: EndpointValue | RecursiveEndpoints;
};

export type ApiEndpoints = EndpointValue | RecursiveEndpoints;

export const API_ENDPOINTS: ApiEndpoints = {
	AUTH: {
		LOGIN: "/auth/login",
		REFRESH: "/auth/token",
		LOGOUT: "/auth/logout"
	},
	USER: {
		FORGOT_PASSWORD: "/users/forgot-password",
		RESET_PASSWORD: (...args: unknown[]) => `/users/reset-password/${args[0]}`,
		ACCEPT_INVITATION: (...args: unknown[]) => `/users/accept-invite/${args[0]}`
	},
	PERMISSIONS: {
		LIST: "/permissions"
	},
	BRAND: {
		USERS: {
			LIST: (...args: unknown[]) => `/brands/${args[0]}/users`,
			GET_BY_USER_ID: (...args: unknown[]) =>
				`/brands/${args[0]}/users/${args[1]}`,
			CREATE: (...args: unknown[]) => `/brands/${args[0]}/users`,
			UPDATE_BY_ID: (...args: unknown[]) =>
				`/brands/${args[0]}/users/${args[1]}`,
			DELETE_BY_ID: (...args: unknown[]) =>
				`/brands/${args[0]}/users/${args[1]}`
		},
		ROLES: {
			LIST: (...args: unknown[]) => `/brands/${args[0]}/roles`,
			CREATE: (...args: unknown[]) => `/brands/${args[0]}/roles`,
			UPDATE_BY_ID: (...args: unknown[]) =>
				`/brands/${args[0]}/roles/${args[1]}`,
			DELETE_BY_ID: (...args: unknown[]) =>
				`/brands/${args[0]}/roles/${args[1]}`
		}
	},
	SYSTEM: {
		USERS: {
			LIST: "/system/users",
			CREATE: "/system/users",
			UPDATE_BY_ID: (...args: unknown[]) => `/system/users/${args[0]}`,
			DELETE_BY_ID: (...args: unknown[]) => `/system/users/${args[0]}`
		},
		ROLES: {
			LIST: "/system/roles",
			CREATE: "/system/roles",
			UPDATE_BY_ID: (...args: unknown[]) => `/system/roles/${args[0]}`,
			DELETE_BY_ID: (...args: unknown[]) => `/system/roles/${args[0]}`
		}
	},
	BRANDS: {
		LIST: "/brands",
		CREATE: "/onboard",
		UPDATE_BY_ID: (...args: unknown[]) => `/brands/${args[0]}`,
		DELETE_BY_ID: (...args: unknown[]) => `/brands/${args[0]}`
	},
	FLOW_TYPES: {
		LIST: "/flow-types",
		CREATE: "/flow-types",
		GET_BY_ID: (...args: unknown[]) => `/flow-types/${args[0]}`,
		UPDATE_BY_ID: (...args: unknown[]) => `/flow-types/${args[0]}`,
		DELETE_BY_ID: (...args: unknown[]) => `/flow-types/${args[0]}`
	},
	FLOW_ACTIONS: {
		LIST: (...args: unknown[]) => `/flow-types/${args[0]}/flow-actions`,
		CREATE: (...args: unknown[]) => `/flow-types/${args[0]}/flow-actions`,
		GET_BY_ID: (...args: unknown[]) =>
			`/flow-types/${args[0]}/flow-actions/${args[1]}`,
		UPDATE_BY_ID: (...args: unknown[]) =>
			`/flow-types/${args[0]}/flow-actions/${args[1]}`,
		DELETE_BY_ID: (...args: unknown[]) =>
			`/flow-types/${args[0]}/flow-actions/${args[1]}`
	},
	FLOW_TARGETS: {
		LIST: (...args: unknown[]) => `/flow-types/${args[0]}/flow-targets`,
		LIST_BY_BRAND: (...args: unknown[]) =>
			`/flow-types/${args[0]}/flow-targets/brand`,
		CREATE: (...args: unknown[]) => `/flow-types/${args[0]}/flow-targets`,
		GET_BY_ID: (...args: unknown[]) =>
			`/flow-types/${args[0]}/flow-targets/${args[1]}`,
		UPDATE_BY_ID: (...args: unknown[]) =>
			`/flow-types/${args[0]}/flow-targets/${args[1]}`,
		DELETE_BY_ID: (...args: unknown[]) =>
			`/flow-types/${args[0]}/flow-targets/${args[1]}`
	},
	FLOW_DEFINITIONS: {
		LIST: "/flow-definitions",
		CREATE: "/flow-definitions",
		GET_BY_ID: (...args: unknown[]) => `/flow-definitions/${args[0]}`,
		UPDATE_BY_ID: (...args: unknown[]) => `/flow-definitions/${args[0]}`,
		DELETE_BY_ID: (...args: unknown[]) => `/flow-definitions/${args[0]}`
	},
	ENVIRONMENTS: {
		LIST: "/environments",
		CREATE: "/environments",
		GET_BY_ID: (...args: unknown[]) => `/environments/${args[0]}`,
		UPDATE_BY_ID: (...args: unknown[]) => `/environments/${args[0]}`,
		DELETE_BY_ID: (...args: unknown[]) => `/environments/${args[0]}`,
		ROTATE_SECRET: (...args: unknown[]) =>
			`/environments/${args[0]}/rotate-secret`
	},
	FLOWS: {
		LIST: "/flows",
		CREATE: "/flows",
		UPDATE: "/flows",
		DELETE_BY_ID: (...args: unknown[]) => `/flows/${args[0]}`
	},
	RULE: {
		LIST: "/rule",
		CREATE: "/rule",
		GET_BY_ID: (...args: unknown[]) => `/rule/${args[0]}`,
		UPDATE_BY_ID: (...args: unknown[]) => `/rule/${args[0]}`,
		UPDATE_STATUS_BY_ID: (...args: unknown[]) => `/rule/${args[0]}/status`,
		DELETE_BY_ID: (...args: unknown[]) => `/rule/${args[0]}`
	}
};

export function resolveEndpoint(
	apiMap: ApiEndpoints,
	path: string[],
	...args: unknown[]
): string {
	let current: ApiEndpoints = apiMap;

	for (const key of path) {
		if (typeof current !== "object" || current === null || !(key in current)) {
			throw new Error(`Invalid path: ${path.join(" -> ")}`);
		}
		current = current[key];
	}

	if (typeof current === "function") return current(...args);
	if (typeof current === "string") return current;

	throw new Error("Resolved endpoint is not a valid string or function");
}
